/**
 * @fileoverview The dimensions the boolean entry filter can test, resolved
 * against the firm's live configuration (MOR-906 §7.8).
 *
 * Purpose: one place that turns categories, matters, people and grades into
 * `EntryQueryDimension`s, so the widget builder and the report builder offer
 * exactly the same filter vocabulary.
 *
 * Firm-scope filters need matters the caller is not a member of, hence
 * `includeOrgJobs` on `entryContext`.
 *
 * Used by:
 * - src/components/timesheets/WidgetBuilder.tsx
 * - src/components/timesheets/PivotReportBuilder.tsx
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { UNGRADED } from "@cvx/timesheets/query/entryQuery";

import { CategoryGlyph } from "./CategoryBadge";
import type { EntryQueryDimension } from "./EntryQueryBuilder";

const ENTRY_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

export function useEntryQueryDimensions(
  orgId: Id<"organizations">,
  scope: "me" | "org",
) {
  const today = format(new Date(), "yyyy-MM-dd");

  const ready = Boolean(orgId);
  const { data: context } = useQuery({
    ...convexQuery(api.timesheets.entries.entryContext, {
      orgId,
      includeOrgJobs: true,
    }),
    enabled: ready,
  });
  const { data: staff } = useQuery({
    ...convexQuery(api.timesheets.profiles.listOrgStaff, { orgId }),
    enabled: ready,
  });
  const { data: grades } = useQuery({
    ...convexQuery(api.timesheets.grades.listByOrg, { orgId, today }),
    enabled: ready,
  });

  const dimensions = useMemo<EntryQueryDimension[]>(() => {
    // Firm scope sees every live matter; personal scope only the caller's.
    const jobs =
      scope === "org" && (context?.orgJobs ?? []).length > 0
        ? (context?.orgJobs ?? [])
        : (context?.jobs ?? []);

    const out: EntryQueryDimension[] = [
      {
        field: "category",
        label: "Category",
        options: (context?.categories ?? []).map((c) => ({
          value: c.key,
          label: c.name,
        })),
        renderOption: (option) => {
          const category = (context?.categories ?? []).find(
            (c) => c.key === option.value,
          );
          return (
            <span className="flex items-center gap-1.5">
              <CategoryGlyph color={category?.color} icon={category?.icon} />
              {option.label}
            </span>
          );
        },
      },
      {
        field: "job",
        label: "Matter",
        options: jobs.map((j) => ({
          value: j._id as string,
          label: j.jobCode ? `${j.jobCode} — ${j.name}` : j.name,
        })),
      },
      {
        field: "billable",
        label: "Billable",
        options: [
          { value: "true", label: "Billable" },
          { value: "false", label: "Non-billable" },
        ],
      },
      {
        field: "writtenOff",
        label: "Written off",
        options: [
          { value: "true", label: "Written off" },
          { value: "false", label: "Not written off" },
        ],
      },
      {
        field: "status",
        label: "Status",
        options: ENTRY_STATUSES.map((s) => ({ ...s })),
      },
      {
        field: "grade",
        label: "Grade",
        // Grades are matched by the name snapshotted on the entry at
        // approval, so historical time keeps its original grade.
        options: [
          ...(grades ?? []).map((g) => ({ value: g.name, label: g.name })),
          { value: UNGRADED, label: UNGRADED },
        ],
      },
    ];

    // Personal scope only ever reads the caller's own time, so a person
    // condition there could only ever be a no-op or exclude everything.
    if (scope === "org") {
      out.splice(2, 0, {
        field: "user",
        label: "Person",
        options: (staff ?? []).map((m) => ({
          value: m.userId as string,
          label: m.name,
        })),
      });
    }
    return out;
  }, [
    context?.categories,
    context?.jobs,
    context?.orgJobs,
    grades,
    scope,
    staff,
  ]);

  return { context, staff, dimensions };
}
